import type { TodayAppointment } from '@shared/services/appointment/getLists/types/getTodayAppointments.types';
import type { Doctor } from '@shared/services/appointment/makeNewNormal/types/getDoctors.type';
import type { EnrichedInstitution } from '@shared/services/appointment/makeNewNormal/types/getInstitutions.type';
import type { AppointmentSystemParamsResult } from '@shared/services/appointment/makeNewNormal/types/getMessages.type';
import type { GroupedServiceByName } from '@shared/services/appointment/makeNewNormal/types/getServiceTypes.type';
import type { LinkedSlots } from '@shared/services/appointment/makeNewNormal/types/getSlotAppointment.type';
import type { AppointmentPatientInformationAddress } from '@shared/services/appointment/patient/types/getAppointmentPatientInformation.type';

import type { Appointment } from '../../../shared/services/appointment/getLists/types/getAppointmentList.type';
import type { TCUSlot } from '../../../shared/services/appointment/makeNewTCU/types/getTCUSlots.type';
import type {
  FormAnswerQuestion,
  QuestionnaireResult,
} from '../../../shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import type { TCUMakeNewTypeType } from '../AppointmentMakeNew/types/makeNew';

export const AppointmentGroupType = {
  Miss: 'missed',
  Open: 'open',
  Upcoming: 'upcoming',
} as const;

export interface ApiResponseGenericType<ResultType> {
  Error: ErrorResponseType;
  InternalErrorCode: string;
  IsFailure?: boolean;
  IsSuccess?: boolean;
  Result: ResultType;
}

export interface AppointmentDateRange {
  endDate: string;
  startDate: string;
}
export interface AppointmentGroup {
  title: string;
  type: AppointmentGroupTypeType;
}

export type AppointmentGroupTypeType = (typeof AppointmentGroupType)[keyof typeof AppointmentGroupType];
export interface AppointmentSlot {
  endDateTime: string;
  id: string;
  serviceType?: string;
  startDateTime: string;
}

export interface CreateNewAppointment {
  autogenBookedSlot: null | TCUSlot;
  autogenOrgUnit?: null | string;
  errorResponse?: ErrorResponseType | null;
  hsgEnrolment?: string;
  isAcuteVideoConsult: boolean | null; // true: acute; false: chronic, null: not valid VC code
  isAutogenMakeNew?: boolean;
  isCalledTCURequest: boolean;
  isHSGAppointment?: boolean;
  isHsgConsult?: boolean | null;
  isPreApptCompleted: boolean;
  isSingleOrganizationVcService: boolean | null;
  isTCUMakeNew?: boolean;
  isUrtiConsult: boolean;
  selectedDateTime: null | string;
  selectedDoctor: Doctor | null;
  selectedFvInstitution: FvInstitution | null;
  selectedInstitutionCode: null | string;
  selectedLinkedSlots?: LinkedSlots | null;
  selectedLocation: EnrichedInstitution | null;
  selectedServiceType: GroupedServiceByName | null;
  selectedSpecialty: null | Specialty;
  selectedTCUMakeNewType: null | TCUMakeNewTypeType;
  selectedTCUSlots: null | TCUSlot;
  successResponse?: null | SuccessResponseType;
  tcuRequestId?: null | number;
}

export interface GroupedAppointmentSlots {
  slots: AppointmentSlot[];
}

export interface IAppointmentState {
  appointmentDateRange?: AppointmentDateRange | null;
  appointmentType?: null | string;
  createNewAppointment: CreateNewAppointment;
  finalConfirmMessage: null | string;
  hsgTcuRequestId?: null | number;
  isHideQueueReminder: boolean;
  isTrafficLightFlow: boolean;
  mregContactNoUpdatedQnos: string[];
  mregSuccessErrText: null | string;
  mregTodayAppointment: null | TodayAppointment;
  preQuestionnaire: PreQuestionnaire;
  rescheduleAndBookNext: RescheduleAndBookNext;
  selectedAppointment?: Appointment | null;
  selectedSlot?: GroupedAppointmentSlots | null;
  systemParams: AppointmentSystemParamsResult | null;
  vcpPatientDetails: null | VcpPatientDetails;
}

export type LinkedAppointmentSlotsType = Record<string, AppointmentSlot | null>;

export interface PreQuestionnaire {
  answerQuestionnaireValue: FormAnswerQuestion | null;
  currentNumberOfAddButton: Record<string, number>;
  currentQuestionIndex: number;
  isShowButtonBackToSummary: boolean;
  questionnaireValue: null | QuestionnaireResult;
}

export interface ServiceTypeDetails {
  IsAcute: boolean;
  RequestFormType: string;
}

export type ServiceTypeMap = Record<string, ServiceTypeDetails>;

export interface SubmitResultType {
  Code: string;
  Message: string;
  Variants?: null | Variant[];
  VersionCode: number;
}

export interface SuccessResponseType {
  AppointmentType?: string;
  AppointmentTypeMessage?: string;
  Location?: string;
  Practitioner?: string;
  Specialty?: string;
  Start?: string;
  Variants?: null | Variant[];
}

export interface VcpPatientDetails {
  Agreed?: boolean;
  Contact?: string;
  DeliveryAddress?: AppointmentPatientInformationAddress | null;
  Email?: string;
  MainAddress?: AppointmentPatientInformationAddress | null;
  Name?: string;
  PaymentOption?: string;
}

interface ErrorResponseType {
  Code: string;
  Message: string;
  Status: 'E' | 'S';
}

interface FvInstitution {
  clusterCode: string;
  logoCode?: string;
  name: string;
  orgCode: string;
}

interface RescheduleAndBookNext {
  appointmentSubmit?: Appointment | null;
  linkedAppointmentSlotMap: LinkedAppointmentSlotsType;
  selectedLinkedAppointment?: Appointment | null;
}

export const YesNoAnswer = {
  no: 'No',
  notApplicable: 'Not Applicable',
  yes: 'Yes',
} as const;

export const JourneyStatus = {
  Closed: 'Closed',
  Current: 'Current',
  Future: 'Future',
  Missed: 'Missed',
  Past: 'Past',
} as const;

export type JourneyStatusType = (typeof JourneyStatus)[keyof typeof JourneyStatus];

export const HashValue = {
  bookNext: '#bookNext',
  createNew: '#createNew',
  reschedule: '#reschedule',
} as const;

export interface FilloutMyInfoFormValues {
  contact: string;
  fullName: string;
}

export interface InstituteSpecialtySettings {
  OrganizationCode: string;
  SpecialtyTypes: SpecialtySetting[];
}

export interface Specialty {
  name: string;
  visitId: string;
}

export interface SpecialtySetting {
  BeforeCareMessage: string;
  SpecialtyName: string;
  VisitTypeId: string;
}
export interface Variant {
  Code: string;
  Name: string;
}
